import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Ticket, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const KioskoConfigPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ kiosko_id: '', nombre: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kiosko/configs`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error('Error al cargar configuración de quioscos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateField = (kioskoId, field, value) => {
    setItems(prev => prev.map(it => it.kiosko_id === kioskoId ? { ...it, [field]: value } : it));
  };

  const handleSave = async (item) => {
    setSaving(s => ({ ...s, [item.kiosko_id]: true }));
    try {
      await axios.put(`${API}/kiosko/config/${item.kiosko_id}`, {
        nombre: item.nombre,
        activo: item.activo,
        rut_input_enabled: item.rut_input_enabled,
        rut_required: item.rut_required,
        allow_empresa: item.allow_empresa,
        allow_general: item.allow_general,
        show_copy_rut_in_recepcion: item.show_copy_rut_in_recepcion,
      }, { withCredentials: true });
      toast.success(`${item.nombre} actualizado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(s => ({ ...s, [item.kiosko_id]: false }));
    }
  };

  const handleCreate = async () => {
    const id = newForm.kiosko_id.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (!id) { toast.error('El ID es obligatorio'); return; }
    if (!newForm.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      await axios.post(`${API}/kiosko/configs`, {
        kiosko_id: id,
        nombre: newForm.nombre.trim(),
        activo: true,
        rut_input_enabled: false,
        rut_required: false,
        allow_empresa: true,
        allow_general: true,
        show_copy_rut_in_recepcion: true,
      }, { withCredentials: true });
      toast.success('Quiosco creado');
      setNewOpen(false);
      setNewForm({ kiosko_id: '', nombre: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/kiosko/config/${deleteTarget.kiosko_id}`, { withCredentials: true });
      toast.success('Quiosco eliminado');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="kiosko-config-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <Ticket className="w-6 h-6 text-blue-600" /> Configuración de Quioscos
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Controla cómo cada quiosco solicita el RUT del paciente antes de generar el turno
            </p>
          </div>
          <Button onClick={() => setNewOpen(true)} data-testid="btn-new-kiosko">
            <Plus className="w-4 h-4 mr-2" /> Nuevo quiosco
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" /> Cargando...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {items.map(item => (
              <Card key={item.kiosko_id} data-testid={`kiosko-card-${item.kiosko_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-slate-500" />
                      {item.nombre || item.kiosko_id}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={item.activo ? 'default' : 'secondary'}>
                        {item.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                      {item.kiosko_id !== 'kiosko-1' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteTarget(item)}
                          data-testid={`btn-del-${item.kiosko_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 font-mono">{item.kiosko_id}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500">Nombre del quiosco</Label>
                    <Input
                      value={item.nombre || ''}
                      onChange={(e) => updateField(item.kiosko_id, 'nombre', e.target.value)}
                      data-testid={`input-nombre-${item.kiosko_id}`}
                    />
                  </div>

                  <ToggleRow
                    label="Quiosco activo"
                    description="Si está desactivado, el quiosco no genera turnos"
                    checked={item.activo}
                    onCheckedChange={(v) => updateField(item.kiosko_id, 'activo', v)}
                    testId={`switch-activo-${item.kiosko_id}`}
                  />

                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <p className="text-xs font-semibold uppercase text-slate-500 tracking-wide">Captura de RUT</p>
                    <ToggleRow
                      label="Solicitar RUT al paciente"
                      description="Mostrar pantalla de RUT antes de generar turno"
                      checked={item.rut_input_enabled}
                      onCheckedChange={(v) => updateField(item.kiosko_id, 'rut_input_enabled', v)}
                      testId={`switch-rut-enabled-${item.kiosko_id}`}
                    />
                    <ToggleRow
                      label="RUT obligatorio"
                      description="Si está activo, no se puede tomar turno sin RUT"
                      checked={item.rut_required}
                      onCheckedChange={(v) => updateField(item.kiosko_id, 'rut_required', v)}
                      disabled={!item.rut_input_enabled}
                      testId={`switch-rut-required-${item.kiosko_id}`}
                    />
                    <ToggleRow
                      label="Mostrar botón copiar RUT en recepción"
                      description="Facilita pegar el RUT en el sistema RNI"
                      checked={item.show_copy_rut_in_recepcion}
                      onCheckedChange={(v) => updateField(item.kiosko_id, 'show_copy_rut_in_recepcion', v)}
                      testId={`switch-copy-rut-${item.kiosko_id}`}
                    />
                  </div>

                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <p className="text-xs font-semibold uppercase text-slate-500 tracking-wide">Tipos de turno permitidos</p>
                    <ToggleRow
                      label="Permitir paciente general"
                      checked={item.allow_general}
                      onCheckedChange={(v) => updateField(item.kiosko_id, 'allow_general', v)}
                      testId={`switch-general-${item.kiosko_id}`}
                    />
                    <ToggleRow
                      label="Permitir paciente empresa"
                      checked={item.allow_empresa}
                      onCheckedChange={(v) => updateField(item.kiosko_id, 'allow_empresa', v)}
                      testId={`switch-empresa-${item.kiosko_id}`}
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      className="w-full"
                      onClick={() => handleSave(item)}
                      disabled={saving[item.kiosko_id]}
                      data-testid={`btn-save-${item.kiosko_id}`}
                    >
                      {saving[item.kiosko_id] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Guardar cambios
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* New kiosko dialog */}
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo quiosco</DialogTitle>
              <DialogDescription>
                Crea un quiosco adicional. Cada quiosco tiene su propia configuración independiente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>ID del quiosco</Label>
                <Input
                  value={newForm.kiosko_id}
                  onChange={(e) => setNewForm(f => ({ ...f, kiosko_id: e.target.value }))}
                  placeholder="kiosko-2"
                  data-testid="input-new-kiosko-id"
                />
                <p className="text-xs text-slate-500 mt-1">Identificador único (solo letras, números y guiones)</p>
              </div>
              <div>
                <Label>Nombre visible</Label>
                <Input
                  value={newForm.nombre}
                  onChange={(e) => setNewForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Quiosco recepción 2do piso"
                  data-testid="input-new-kiosko-nombre"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreate} data-testid="btn-create-kiosko">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>¿Eliminar quiosco?</DialogTitle>
              <DialogDescription>
                Esta acción no afecta los turnos ya generados.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="btn-confirm-del-kiosko">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const ToggleRow = ({ label, description, checked, onCheckedChange, disabled, testId }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="flex-1">
      <p className="text-sm font-medium text-slate-800">{label}</p>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
    <Switch
      checked={!!checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      data-testid={testId}
    />
  </div>
);

export default KioskoConfigPage;
